import React, { Component } from 'react';
import { observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link } from '@reach/router';
import { keys } from 'lodash';

import { makeRequest, errorResponse } from 'js/services/Network';
import { lightGrey3Color } from 'js/config/style';

import CountDown from 'js/components/core/CountDown';
import LoadingSpinner from '../../LoadingSpinner';

import Container from './Container';
import Title from './Title';
import Content from './Content';
import Display from './Display';
import Label from './Label';
import Image from './Image';
import LogoIcon from './LogoIcon';

import orderStatusTranslation from 'js/config/orderStatusTranslation';

@observer
export default class Order extends Component {
  constructor(props) {
    super(props);
    this.load(props.load);
  }

  @observable error = null;
  @observable isLoading = true;
  @observable.ref data = null;

  hasLoadProps = () =>
    keys(this.props.load || {}).reduce((hasLoadProps, prop) => hasLoadProps && !!this.props.load[prop], true);

  @action async load(props) {
    if (this.hasLoadProps()) {
      this.isLoading = true;
      this.error = null;

      try {
        const data = (await makeRequest('admin-order-data', null, props)).data;
        runInAction(() => (this.data = data));
      } catch (error) {
        runInAction(() => (this.error = errorResponse(error)));
      }

      runInAction(() => (this.isLoading = false));
    } else {
      this.error = 'Could not load order';
      this.isLoading = false;
    }
  }

  render() {
    const data = this.data;

    return (
      <Container mode={this.isLoading ? 'loading' : this.error ? 'error' : 'display'}>
        {this.isLoading ? (
          <LoadingSpinner padding="1rem" />
        ) : this.error ? (
          <>
            <Display>
              <LogoIcon name="archive" color={lightGrey3Color} />
            </Display>
            <Title>{this.props.title || 'Order'}</Title>
            <Content title={this.error}>Order not found</Content>
          </>
        ) : (
          <>
            <Display>
              <Image src={data.image} />
            </Display>
            <Title>
              <Link to={`/admin/orders/order/${data.uid}`}>{this.props.title || 'Order'}</Link>
            </Title>
            <Content>
              <Label>Order ID</Label>
              <div>
                <Link to={`/admin/orders/order/${data.uid}`}>{data.uid}</Link>
              </div>

              <Label>Status</Label>
              <div>
                <div>{orderStatusTranslation[data.status] || data.status}</div>

                <div>
                  {data.status === 'offer_requested_by_buyer' && (
                    <CountDown remaining={data.seconds_until_expiration}>
                      {({ status, duration, timeLeft, Remaining }) =>
                        status === 'expired' ? (
                          <>
                            (offer{' '}
                            <Remaining status={status} title={data.expiration_time}>
                              expired
                            </Remaining>
                            )
                          </>
                        ) : (
                          <>
                            (expires in{' '}
                            <Remaining status={status} title={data.expiration_time}>
                              {status === 'warn' ? timeLeft : duration.humanize()}
                            </Remaining>
                            )
                          </>
                        )
                      }
                    </CountDown>
                  )}

                  {data.status === 'delivered_to_buyer' && (
                    <CountDown remaining={data.seconds_until_completion}>
                      {({ status, duration, timeLeft, Remaining }) =>
                        status === 'expired' ? (
                          <>
                            (order{' '}
                            <Remaining status={status} title={data.completion_time}>
                              completed
                            </Remaining>
                            )
                          </>
                        ) : (
                          <>
                            (completes in{' '}
                            <Remaining status={status} title={data.completion_time}>
                              {status === 'warn' ? timeLeft : duration.humanize()}
                            </Remaining>
                            )
                          </>
                        )
                      }
                    </CountDown>
                  )}

                  {data.status === 'order_return_delivered' && (
                    <CountDown remaining={data.seconds_until_returned}>
                      {({ status, duration, timeLeft, Remaining }) =>
                        status === 'expired' ? (
                          <>
                            (order{' '}
                            <Remaining status={status} title={data.returned_time}>
                              returned
                            </Remaining>
                            )
                          </>
                        ) : (
                          <>
                            (returned in{' '}
                            <Remaining status={status} title={data.returned_time}>
                              {status === 'warn' ? timeLeft : duration.humanize()}
                            </Remaining>
                            )
                          </>
                        )
                      }
                    </CountDown>
                  )}
                </div>
              </div>

              <Label>Buyer</Label>
              <div>
                <Link to={`/admin/users/user/${data.buyer_email}`}>{data.buyer_name}</Link>
              </div>

              <Label>Seller</Label>
              <div>
                <Link to={`/admin/users/user/${data.seller_email}`}>{data.seller_name}</Link>
              </div>
            </Content>
          </>
        )}
      </Container>
    );
  }
}
